import { memo, useEffect, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { formatBarDate, formatClock } from '../../lib/format.ts';
import { useShows } from '../BarPlanContext.tsx';
import { msUntilNextTick } from '../clock.ts';
import { useInfoBarPrefs } from '../prefsStore.ts';
import type { WidgetVariant } from './types.ts';

/** Re-renders at each minute (or second) boundary; stops while the tab is hidden and catches up when it returns. */
export function useNow(seconds: boolean): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      timer = setTimeout(() => {
        setNow(new Date());
        schedule();
      }, msUntilNextTick(Date.now(), seconds));
    };
    const onVisibility = () => {
      clearTimeout(timer);
      if (document.visibilityState === 'hidden') return;
      setNow(new Date());
      schedule();
    };
    setNow(new Date());
    if (document.visibilityState !== 'hidden') schedule();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [seconds]);
  return now;
}

/** Owns the clock state so the rest of the bar never re-renders with it. */
export const ClockWidget = memo(function ClockWidget({ variant }: { variant: WidgetVariant }) {
  const { showSeconds: seconds, showDate } = useInfoBarPrefs();
  const now = useNow(seconds);
  const shows = useShows();
  return (
    <time className="iw iw-clock" data-variant={variant} dateTime={now.toISOString()}>
      {showDate && shows('date') && (
        <span className="iw-date">
          <CalendarDays size={15} aria-hidden />
          {formatBarDate(now)}
        </span>
      )}
      <span className="iw-time">{formatClock(now, seconds)}</span>
    </time>
  );
});

export const ClockTime = memo(function ClockTime() {
  const seconds = useInfoBarPrefs().showSeconds;
  const now = useNow(seconds);
  return (
    <time className="iw-time" dateTime={now.toISOString()}>
      {formatClock(now, seconds)}
    </time>
  );
});
